import { Dispatch, ReactNode, SetStateAction } from 'react';
import { Alignment, LineWidgetData, Side } from 'widgets/Lines/LineWidget/LineWidget.types';
import { AllWidgetData, NewWidget } from 'widgets/Widget.types';

import { WidgetId } from './idTypes';
import { Subset } from './object.types';

export enum PortTypes {
  Creator = 'creator',
  Connector = 'connector',
}

export enum OnChangeEventTypes {
  Update = 'update',
  Done = 'done',
}

export interface GroupTargetData {
  groupId: string;
  wrapperPos: WrapperPos;
}

export type UpdateLineWidget = { widgetId: WidgetId; widgetData: Subset<LineWidgetData> };

export type PortProps = {
  size: number;
  isHover: boolean;
};

export type PortSelectorProps = {
  size: { widthPx: number; heightPx: number };
};

export type PortState = {
  widgetId: WidgetId;
  side: Side;
  alignment: Alignment;
};

export type WrapperPos = {
  leftPx: number;
  topPx: number;
  widthPx: number;
  heightPx: number;
};

export type PortWrapperData = {
  widgetId: WidgetId;
  position: WrapperPos;
  rotation?: number;
  includeCenter?: boolean;
};

export type NodeCreatorModalData = {
  widgetId: WidgetId;
  position: WrapperPos;
  lineWidgetData: NewWidget;
};

/**
 * FlowCoreContext
 */
export type GetConnectedLinesType = (
  widgetId: WidgetId,
  target: HTMLElement,
  shouldCheckConnection?: boolean,
) => UpdateLineWidget[];

export type OnChangeEvent = (type: OnChangeEventTypes, matrix: DOMMatrix, target: HTMLElement) => void | LineWidgetData;

export type SubscriptionType = [WidgetId, OnChangeEvent];

export type ConnectedWidgetData = { widgetId: WidgetId; widgetData: AllWidgetData };

export type ConnectedLineData = { [key: WidgetId]: WidgetId[] };

export interface FlowCoreState {
  isObserving: boolean;
  isActiveWidgetLinesConnected: boolean;
  isActiveWidgetsHasLineConnected: boolean;
  flowCoreEvent: { onChange: (widgetId: WidgetId, callback: OnChangeEvent) => void };
  getConnectedLines: GetConnectedLinesType;
  startFlowMode: () => void;
  stopFlowMode: () => void;
  getisActiveWidgetsHasLineConnected: () => boolean;
  getConnectedLineUpdatesToWidget: (widgetId: WidgetId) => UpdateLineWidget[];
}

export interface FlowCoreContextI {
  value: FlowCoreState;
}

export interface FlowCoreProviderProps {
  children: ReactNode;
}

/**
 * For PortRenderer
 */

export interface PortProviderProps {
  children: ReactNode;
}

export interface PortRendererProps {
  zoom: number;
}

export interface PortContextState {
  connectorPortData: PortWrapperData | null;
  creatorPortData: PortWrapperData | null;
  selectedPort: PortState | null;
  nodeModalData: NodeCreatorModalData | null;
  setSelectedPort: Dispatch<SetStateAction<PortState | null>>;
  getSelectedPort: () => PortState | null;
  renderPort: (
    targetElm: HTMLElement | null,
    type: PortTypes,
    groupWrapperData?: GroupTargetData,
    // Optional - if true, will render the center port
    includeCenter?: boolean,
  ) => void;
  clearPort: () => void;
  renderModal: (nodeCreatorModalData: NodeCreatorModalData | null) => void;
  closeModal: () => void;
  // render the port around a group widget
  renderGroupPort: (widgetId: WidgetId) => void;
  updatePortPosition: (WidgetId: WidgetId) => void;
}

export interface PortContextI {
  value: PortContextState;
}
